/**
 * Where one borrower's quarter stands, in the words an analyst reads it in
 * (DOP-625), and the one thing to do next.
 *
 * The board's row, its tiles and the Needs you tab all read this, so a row can
 * never say one thing while a tile counts it as another; a tile counts exactly
 * the rows that say the thing it names — the rule `dealStanding` follows for
 * deal screening.
 *
 * Order matters: a quarter still waiting on its package says so before
 * anything about covenants; a run with open checks leads with them, since a
 * quarter cannot be locked while one is open; a breach is shown even when
 * nothing is open, because it is what a reader must see first.
 */

import type {
	MonitoringBoardRow,
	MonitoringCovenantResult,
	MonitoringPeriod,
} from "../../types/monitoring";
import { formatIsoDate } from "./monitoringFormat";

export type BorrowerStandingKind =
	| "waiting"
	| "late"
	| "ready_to_run"
	| "running"
	| "failed"
	| "checks"
	| "breach"
	| "near"
	| "pass"
	| "locked";

/**
 * The board's one button. Every quarter with a finished run is Open until someone locks it
 * on the run page; the board never locks, even when every covenant passes.
 */
export type NextStepKind = "open" | "locked" | "run" | "wait";

export interface BorrowerStanding {
	kind: BorrowerStandingKind;
	/** The status in plain words: "Breach", "Near threshold", "All pass". Covenant names and
	 * figures live on the run page, not in the board's status. */
	label: string;
	/** A second line, only when it says what to do or wait for: "2 checks open". */
	detail: string | null;
	next: NextStepKind;
}

const WORST_ORDER = ["breach", "near", "unknown", "pass"] as const;

/** The covenant the worst outcome comes from, so the row can name it. */
export function worstCovenant(row: MonitoringBoardRow): MonitoringCovenantResult | null {
	for (const outcome of WORST_ORDER) {
		const hit = row.covenants.results.find((result) => result.result === outcome);
		if (hit) return hit;
	}
	return null;
}

const checksLabel = (count: number): string =>
	count === 1 ? "1 check open" : `${count} checks open`;

/** Whole days from `iso` to today, or null when it is not in the past. */
export function daysPast(iso: string, today: Date = new Date()): number | null {
	const due = new Date(`${iso}T00:00:00`);
	if (Number.isNaN(due.getTime())) return null;
	const days = Math.floor((today.getTime() - due.getTime()) / 86_400_000);
	return days > 0 ? days : null;
}

export function borrowerStanding(
	row: MonitoringBoardRow,
	period: Pick<MonitoringPeriod, "package_due">,
	today: Date = new Date(),
): BorrowerStanding {
	const run = row.run;
	const due = formatIsoDate(period.package_due);

	if (!run) {
		if (row.package.status === "not_received") {
			if (row.package.overdue) {
				const days = daysPast(period.package_due, today);
				return {
					kind: "late",
					label: days ? `Package ${days} ${days === 1 ? "day" : "days"} late` : "Package late",
					detail: `Was due ${due}`,
					next: "wait",
				};
			}
			return { kind: "waiting", label: "Waiting for the package", detail: `Due ${due}`, next: "wait" };
		}
		return {
			kind: "ready_to_run",
			label: row.package.status === "partial" ? "Part of the package in · not run" : "Package in · not run yet",
			detail: `${row.package.documents.length} documents`,
			next: "run",
		};
	}
	if (run.generating) return { kind: "running", label: "Running…", detail: null, next: "wait" };
	if (run.generation_error) {
		return { kind: "failed", label: "Run failed", detail: run.generation_error, next: "run" };
	}

	const worst = worstCovenant(row);

	if (run.status === "locked") {
		return {
			kind: "locked",
			label:
				worst?.result === "breach" || row.covenants.worst === "breach"
					? "Locked · breach"
					: row.covenants.worst === "pass"
						? "Locked · all pass"
						: "Locked",
			detail: null,
			next: "locked",
		};
	}
	if (worst?.result === "breach" || row.covenants.worst === "breach") {
		return {
			kind: "breach",
			label: "Breach",
			detail: run.gaps_open > 0 ? checksLabel(run.gaps_open) : null,
			next: "open",
		};
	}
	if (run.gaps_open > 0) {
		return {
			kind: "checks",
			label: checksLabel(run.gaps_open),
			detail: worst?.result === "near" ? "Near threshold" : "Needed before the quarter can be locked",
			next: "open",
		};
	}
	if (worst?.result === "near") {
		return { kind: "near", label: "Near threshold", detail: null, next: "open" };
	}
	return {
		kind: "pass",
		label: row.covenants.worst === "pass" ? "All pass" : "No covenant result",
		detail: null,
		next: "open",
	};
}

/** The standings that wait on a person this quarter (the Needs you tab and its count). */
const NEEDS_YOU: ReadonlySet<BorrowerStandingKind> = new Set([
	"checks",
	"breach",
	"ready_to_run",
	"failed",
	"late",
]);

export const needsYou = (standing: BorrowerStanding): boolean => NEEDS_YOU.has(standing.kind);
